// src/pages/client/ClientProfile.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiSave,
  FiEdit,
  FiHome,
  FiDroplets,
  FiLeaf,
  FiMessageCircle,
} from "react-icons/fi";
import { useClient } from "../context/ClientContext";
import axios from "axios";

function ClientProfile() {
  const { client } = useClient();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [profileData, setProfileData] = useState({
    personalInfo: {
      fullName: "",
      address: "",
      location: {
        address: "",
      },
    },
    farmDetails: {
      farmName: "",
      farmSize: "",
      soilType: "",
      irrigationSystem: "",
      crops: [],
      livestock: [],
    },
    communication: {
      preferredMethod: "email",
    },
  });

  useEffect(() => {
    if (client?.profile) {
      setProfileData(client.profile);
    }
  }, [client]);

  const handleInputChange = (section, field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNestedChange = (section, nestedField, field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedField]: {
          ...prev[section][nestedField],
          [field]: value,
        },
      },
    }));
  };

  const handleArrayChange = (section, field, value) => {
    const array = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);
    setProfileData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: array,
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.put(
        "https://agri-back-mauve.vercel.app/api/client-portal/profile",
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setMessage("Profile updated successfully!");
        setIsEditing(false);
        // Update context if needed
      }
    } catch (error) {
      setMessage(
        "Error updating profile: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const communicationMethods = [
    { value: "email", label: "Email", icon: FiMail },
    { value: "sms", label: "SMS", icon: FiPhone },
    { value: "whatsapp", label: "WhatsApp", icon: FiMessageCircle },
  ];

  const soilTypes = [
    "Sandy",
    "Clay",
    "Loamy",
    "Silty",
    "Peaty",
    "Chalky",
    "Saline",
  ];

  const irrigationSystems = [
    "Drip Irrigation",
    "Sprinkler System",
    "Surface Irrigation",
    "Center Pivot",
    "Manual Watering",
    "Rain-fed",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                Profile & Farm Details
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <FiEdit size={16} />
                  <span>Edit Profile</span>
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                  >
                    <FiSave size={16} />
                    <span>{loading ? "Saving..." : "Save Changes"}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.includes("Error")
                ? "bg-red-50 border border-red-200 text-red-700"
                : "bg-green-50 border border-green-200 text-green-700"
            }`}
          >
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FiUser className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Personal Information
                </h2>
                <p className="text-gray-600">
                  Your contact details and location
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profileData.personalInfo.fullName || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "personalInfo",
                      "fullName",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="flex items-center space-x-2">
                  <FiMail className="text-gray-400" />
                  <span className="text-gray-900">{client?.email}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Contact support to change email
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex items-center space-x-2">
                  <FiPhone className="text-gray-400" />
                  <span className="text-gray-900">{client?.phone}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Contact support to change phone
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  value={profileData.personalInfo.address || ""}
                  onChange={(e) =>
                    handleInputChange("personalInfo", "address", e.target.value)
                  }
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="Enter your complete address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Farm Location
                </label>
                <div className="flex items-center space-x-2">
                  <FiMapPin className="text-gray-400" />
                  <input
                    type="text"
                    value={profileData.personalInfo.location?.address || ""}
                    onChange={(e) =>
                      handleNestedChange(
                        "personalInfo",
                        "location",
                        "address",
                        e.target.value
                      )
                    }
                    disabled={!isEditing}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder="Farm location address"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Farm Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FiHome className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Farm Details
                </h2>
                <p className="text-gray-600">Information about your farm</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Farm Name
                </label>
                <input
                  type="text"
                  value={profileData.farmDetails.farmName || ""}
                  onChange={(e) =>
                    handleInputChange("farmDetails", "farmName", e.target.value)
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Farm Size
                </label>
                <input
                  type="text"
                  value={profileData.farmDetails.farmSize || ""}
                  onChange={(e) =>
                    handleInputChange("farmDetails", "farmSize", e.target.value)
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="e.g., 50 acres, 20 hectares"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soil Type
                </label>
                <select
                  value={profileData.farmDetails.soilType || ""}
                  onChange={(e) =>
                    handleInputChange("farmDetails", "soilType", e.target.value)
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Select soil type</option>
                  {soilTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Irrigation System
                </label>
                <select
                  value={profileData.farmDetails.irrigationSystem || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "farmDetails",
                      "irrigationSystem",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Select irrigation system</option>
                  {irrigationSystems.map((system) => (
                    <option key={system} value={system}>
                      {system}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Crops Grown
                </label>
                <input
                  type="text"
                  value={profileData.farmDetails.crops?.join(", ") || ""}
                  onChange={(e) =>
                    handleArrayChange("farmDetails", "crops", e.target.value)
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="e.g., Maize, Beans, Coffee, Tea"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate multiple crops with commas
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Livestock
                </label>
                <input
                  type="text"
                  value={profileData.farmDetails.livestock?.join(", ") || ""}
                  onChange={(e) =>
                    handleArrayChange(
                      "farmDetails",
                      "livestock",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="e.g., Dairy Cattle, Poultry, Goats"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate multiple types with commas
                </p>
              </div>
            </div>
          </motion.div>

          {/* Communication Preferences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FiMessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Communication Preferences
                </h2>
                <p className="text-gray-600">
                  How you'd like to receive updates and notifications
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {communicationMethods.map((method) => {
                const Icon = method.icon;
                return (
                  <label
                    key={method.value}
                    className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      profileData.communication.preferredMethod === method.value
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    } ${!isEditing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="radio"
                      name="preferredMethod"
                      value={method.value}
                      checked={
                        profileData.communication.preferredMethod ===
                        method.value
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "communication",
                          "preferredMethod",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg ${
                          profileData.communication.preferredMethod ===
                          method.value
                            ? "bg-purple-100 text-purple-600"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <Icon size={20} />
                      </div>
                      <span className="font-medium text-gray-900">
                        {method.label}
                      </span>
                    </div>
                    {profileData.communication.preferredMethod ===
                      method.value && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-purple-500 rounded-full"></div>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">
                Notification Types
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Project updates and milestones</li>
                <li>• New reports and recommendations</li>
                <li>• Appointment reminders</li>
                <li>• Important announcements</li>
                <li>• Payment reminders and invoices</li>
              </ul>
            </div>
          </motion.div>
        </div>

        {!isEditing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6"
          >
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <FiEdit className="w-3 h-3 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">
                  Profile Update Required
                </h3>
                <p className="text-yellow-700 text-sm">
                  Please ensure your profile information is up to date. Accurate
                  farm details help us provide better recommendations and
                  services. Some information may require admin approval for
                  changes.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ClientProfile;
